import { KycStatus } from '@getstep/sdk/dist/api/generated/CommonProto'
import classNames from 'classnames'
import dynamic from 'next/dynamic'
import Link from 'next/link'
import Router, { useRouter } from 'next/router'
import type { FunctionComponent } from 'react'
import React, { useEffect, useMemo, useRef, useState } from 'react'

import { ActionButton } from '../../../contentful-components/action-button'
import { DownloadApp } from '../../../contentful-components/forms/download-app'
import { useBodyLock } from '../../../lib/hooks/useBodyLock'
import { helpCenterStore } from '../../../lib/store/ui/help-center'
import { uiStore } from '../../../lib/store/ui/ui'
import { useStores } from '../../../lib/store/useStores'
import {
    isFundingPage,
    isKYCPage,
    isPaymentFunnelPage,
} from '../../../lib/utils/page'
import { TEXT } from '../../../lib/utils/text'
import { ThemeMode } from '../../../lib/utils/theme'
import { LINK, LocalLink } from '../../../shared/util/link'
import { iconSizeSmall } from '../../../styles/variables.module.scss'
import { RawAnchor } from '../../anchor'
import { LinkButton } from '../../button/link'
import { GetStepButton } from '../../get-step-button'
import { HamburgerButton } from '../../hamburger-button'
import { Help } from '../../icons/help'
import { Logo } from '../../icons/logo'
import { Desktop, Mobile } from '../../responsive'
import { Text } from '../../text'
import { DropdownMenu } from '../dropdown-menu'
import { Container } from '../grid'
import { MAIN_NAVIGATION_LINKS } from '../navigation-links'
import styles from './header.module.scss'
import { TeamEnvironmentBanner } from './team-env-banner'

const Sidebar = dynamic(() => import('./sidebar'), {
    ssr: false,
})

export const Header: FunctionComponent<{ hasBanner?: boolean }> = ({
    hasBanner = false,
}) => {
    const outerRef = useRef<HTMLDivElement>(null)
    const ref = useRef<HTMLDivElement>(null)

    const router = useRouter()
    const [isOpen, setIsOpen] = useState(false)
    const { linkingStore } = useStores()

    // lock scroll when mobile nav is open
    useBodyLock(isOpen)

    // always close navigation on route change
    useEffect(() => {
        const handleRouteChange = () => {
            setIsOpen(false)
        }

        Router.events.on('routeChangeStart', handleRouteChange)

        return () => {
            Router.events.off('routeChangeStart', handleRouteChange)
        }
    }, [])

    // handle header position when banner is present
    useEffect(() => {
        const handleScroll = () => {
            if (!hasBanner || !outerRef.current || !ref.current) return

            const outerRect = outerRef.current.getBoundingClientRect()

            const outerTop = outerRect.top + window.scrollY
            const refDistanceToTop = outerTop - window.scrollY

            if (refDistanceToTop > 0 && refDistanceToTop < outerTop) {
                ref.current.style.top = `${refDistanceToTop}px`
            } else if (refDistanceToTop <= 0) {
                ref.current.style.top = '0px'
            } else {
                ref.current.style.top = `${outerTop}px`
            }
        }

        if (hasBanner) {
            handleScroll()
            window.addEventListener('scroll', handleScroll)
        } else {
            // account for when user manually closes banner
            if (ref.current) ref.current.style.top = '0px'
            window.removeEventListener('scroll', handleScroll)
        }

        return () => {
            window.removeEventListener('scroll', handleScroll)
        }
    }, [hasBanner])

    const handleCloseClick = () => {
        setIsOpen(false)
    }

    const isKycFlow = isKYCPage(router.pathname)
    const isPaymentFlow = router.isReady && isPaymentFunnelPage(router.asPath)
    const isFundingFlow = isFundingPage(router.pathname)
    const showNavigation = !isKycFlow && !isPaymentFlow && !isFundingFlow

    const ctaButton = useMemo(
        () => {
            if (isFundingFlow) return <FundingActionButton />
            if (isKycFlow) return <KycActionButton />
            if (isPaymentFlow) return <PaymentActionButton />

            return (
                <div className={styles.buttons}>
                    <div className={styles['action-button']}>
                        <Mobile>
                            <GetStepButton size='small' />
                        </Mobile>
                        <Desktop>
                            <DownloadApp
                                analyticsKey='getApp.button'
                                placement='header'
                                title={TEXT.getStarted}
                                size='medium'
                            />
                        </Desktop>
                    </div>
                    <Mobile>
                        <HamburgerButton
                            open={isOpen}
                            onClick={() => setIsOpen(!isOpen)}
                        />
                    </Mobile>
                </div>
            )
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            isFundingFlow,
            isKycFlow,
            isOpen,
            isPaymentFlow,
            linkingStore.appDownloadLink,
        ]
    )

    return (
        <header ref={outerRef}>
            <TeamEnvironmentBanner />
            <Container
                ref={ref}
                className={styles.wrapper}
                data-testid='layout.header'
            >
                <nav className={styles.header}>
                    <div className={styles.inner}>
                        <Link href='/' passHref>
                            <Logo
                                width='52'
                                height='42'
                                alt='Step logo'
                                preset={
                                    uiStore.mode === ThemeMode.Black
                                        ? 'white'
                                        : 'color'
                                }
                            />
                        </Link>
                        <div className={styles.right}>
                            <Desktop visible={showNavigation}>
                                <div className={classNames(styles.list)}>
                                    {MAIN_NAVIGATION_LINKS.map((route) => {
                                        const { label, href, items } = route

                                        if (items && items?.length > 0) {
                                            return (
                                                <DropdownMenu
                                                    key={label}
                                                    id={label}
                                                    items={items.map(
                                                        ({ label, href }) => ({
                                                            text: label,
                                                            url: new LocalLink({
                                                                base: href,
                                                            }),
                                                        })
                                                    )}
                                                    label={label}
                                                />
                                            )
                                        }

                                        const isActive =
                                            router.pathname === href

                                        return (
                                            <Link
                                                key={label}
                                                href={href}
                                                passHref
                                                className={styles.link}
                                            >
                                                <Text
                                                    tag='span'
                                                    typeStyle={{
                                                        _: 'body-2xs',
                                                        md: 'body-xs',
                                                    }}
                                                    bold
                                                    color={
                                                        isActive
                                                            ? 'primary'
                                                            : 'text'
                                                    }
                                                >
                                                    {label}
                                                </Text>
                                            </Link>
                                        )
                                    })}
                                </div>
                            </Desktop>

                            {ctaButton}
                        </div>
                    </div>
                </nav>

                {showNavigation && (
                    <Sidebar
                        links={MAIN_NAVIGATION_LINKS}
                        open={isOpen}
                        onOverlayClick={handleCloseClick}
                    />
                )}
            </Container>
        </header>
    )
}

export default Header

const FundingActionButton = () => (
    <LinkButton
        href={LINK.kycResult(KycStatus.SUCCESS).serialize()}
        preset='secondary'
        size='medium'
        analyticsKey='funding.skip'
    >
        {TEXT.skip}
    </LinkButton>
)

const KycActionButton = () => (
    <>
        <Mobile>
            <RawAnchor
                href={LINK.help(helpCenterStore.screen)}
                className={styles.help}
                target='_blank'
                analyticsKey='kyc.help'
            >
                <Help width={iconSizeSmall} />
            </RawAnchor>
        </Mobile>
        <Desktop>
            <LinkButton
                href={LINK.help(helpCenterStore.screen)}
                preset='secondary'
                size='medium'
                target='_blank'
                analyticsKey='kyc.help'
            >
                {TEXT.help}
            </LinkButton>
        </Desktop>
    </>
)

const PaymentActionButton = () => (
    <ActionButton
        analyticsKey='getApp.button'
        type='ActionDownloadApp'
        placement='header'
        title={TEXT.payOnStep}
        size='medium'
    />
)
